'use client';
import React, { useState } from 'react';
import Slider from 'react-slick';
import ArrowRightIcon from '../Icons/Action/ArrowRightIcon';
import BookingCard from './BookingCard';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import type { BookingType } from '@/types/booking';
import { T } from '@tolgee/react';
import Link from 'next/link';

const NextArrow = ({ onClick, isVisible }: { onClick?: () => void; isVisible: boolean }) => (
  <div
    className={`absolute right-6 z-10 top-0 bottom-0 m-auto cursor-pointer rounded-[100px] border border-white bg-white/30 backdrop-blur-[10px] w-[60px] h-[60px] flex items-center justify-center transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
    onClick={onClick}
  >
    <ArrowRightIcon width={20} height={20} />
  </div>
);

const PrevArrow = ({ onClick, currentSlide }: { onClick?: () => void; currentSlide: number }) =>
  currentSlide > 0 ? (
    <div
      className="absolute left-6 z-10 top-0 bottom-0 m-auto cursor-pointer rounded-[100px] border border-white bg-white/30  backdrop-blur-[10px] w-[60px] h-[60px] flex items-center justify-center"
      onClick={onClick}
    >
      <ArrowRightIcon width={20} height={20} className="transform rotate-180" />
    </div>
  ) : null;

interface SliderProps {
  items: BookingType[];
  title?: string;
  seeAllLink?: string;
  expertView?: boolean;
  expertExternalId?: string;
}

const BookingSlider: React.FC<SliderProps> = ({
  items,
  title,
  seeAllLink,
  expertView = false,
  expertExternalId,
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const slideCount = items.length;
  const settings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 2.06,
    slidesToScroll: 1,
    centerMode: false,
    nextArrow: <NextArrow isVisible={currentSlide < slideCount - 2} />,
    prevArrow: <PrevArrow currentSlide={currentSlide} />,
    beforeChange: (current: number, next: number) => setCurrentSlide(Math.round(next)),
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2.06,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1.2,
          slidesToScroll: 1,
        },
      },
    ],
  };

  return (
    <div className="relative p-8 w-full">
      {title && (
        <div className="relative">
          <h2 className="text-4xl">{title}</h2>
          {seeAllLink && (
            <Link
              href={seeAllLink}
              className="absolute top-4 right-14 text-sm active-link main-link hidden md:flex"
            >
              <T>rebook_section.view_all.label</T>
            </Link>
          )}
        </div>
      )}
      <Slider {...settings} className="mt-4">
        {items.map((item, index) => (
          <div key={index}>
            <BookingCard {...item} expertView={expertView} expertExternalId={expertExternalId} />
          </div>
        ))}
      </Slider>
    </div>
  );
};

export default BookingSlider;
